import { Box, Button } from '@mui/material'
import { FileUploadStatusLabel } from '~/src/components/molecules'
import UploadIcon from '@mui/icons-material/Upload'
import React, { useEffect, useState } from 'react'
import { useFileUpload, useMessage } from '~/src/hooks'
import { FileUploaderDialog } from '~/src/components/organisms/FileUploaderDialog/FileUploaderDialog'
import { useTranslation } from 'react-i18next'
import { containerRepository } from '~/src/repositories'
import DriveFileRenameOutlineOutlinedIcon from '@mui/icons-material/DriveFileRenameOutlineOutlined'
import { InputDialog } from '~/src/components/organisms/InputDialog/InputDialog'
import {
  StyledBox,
  StyledBoxOther,
  StyledTypography,
  StyledTypographyOther,
  StyledTypographyUpload,
  StyledIconButtonOther,
} from './ContainerOrderFileUpload.styles'
import { useSWRConfig } from 'swr'

type FileObj = {
  id: number
  name: string
  displayOrder: string | number | null
  file: {
    filename: string
    url: string
  }
}

interface Props {
  containerId: number
  label: string
  fileType: 'blDraft' | 'surrenderBl' | 'packingList' | string
  fileMeta: FileObj | undefined
  displayOrder?: number
}

/**
 * ContainerOrderFileUpload
 * @param containerId
 * @param label
 * @param fileType blDraft | surrenderBl | packingList
 * @param fileMeta
 * @constructor
 */
export const ContainerOrderFileUpload: React.VFC<Props> = ({
  containerId,
  label,
  fileType,
  fileMeta,
  displayOrder = 0,
}) => {
  // console.log(fileMeta)
  const { t } = useTranslation()
  const { handleOpenWithSuccessMessage, handleOpenWithErrorMessage } = useMessage()
  const { uploadFile, setUploadFile, isFileUpLoading, handleFileChange, handleFileDelete } =
    useFileUpload(fileMeta?.file)
  const [open, setOpen] = React.useState(false)
  const [openInput, setOpenInput] = React.useState(false)
  const [labelValue, setLabelValue] = useState<string>(fileMeta?.name || '')
  const { mutate } = useSWRConfig()

  /**
   * convertFileType
   * 1: PACKING_LIST 2: SURRENDER_BL 3: BILL 4: BL_DRAFT 5: IMAGE 6: OTHER
   * @param fileType
   *
   */
  const convertFileType = (fileType: string): number => {
    switch (fileType) {
      case 'packingList':
      case 'PACKING_LIST':
        return 1
      case 'surrenderBl':
      case 'SURRENDER_BL':
        return 2
      case 'bill':
      case 'BILL':
        return 3
      case 'blDraft':
      case 'BL_DRAFT':
        return 4
      case 'image':
      case 'IMAGE':
        return 5
      case 'other':
      case 'OTHER':
        return 6
      default:
        return 0
    }
  }

  useEffect(() => {
    if (fileMeta?.file) {
      setUploadFile(fileMeta.file)
    }
    if (fileMeta?.name) {
      setLabelValue(fileMeta.name)
    }
  }, [fileMeta])

  useEffect(() => {
    if (uploadFile?.url.includes('temp/')) {
      handleSubmitUploadFile()
    }
  }, [uploadFile])

  /**
   * handleSubmitUpdateFileName
   */
  const handleSubmitUpdateFileName = async () => {
    if (labelValue === '') {
      return handleOpenWithErrorMessage('Label名を入力してください')
    }
    if (!fileMeta?.id) {
      setLabelValue('')
      return handleOpenWithErrorMessage('ファイルを先に保存してください')
    }
    try {
      await containerRepository.patchContainerFileName(fileMeta.id, labelValue)
      handleOpenWithSuccessMessage('Label名を更新しました')
      setOpenInput(false)
    } catch (e) {
      console.log(e)
      handleOpenWithErrorMessage('Label名の更新に失敗しました')
    }
  }
  /**
   * handleSubmitUpdateFileName
   */
  const handleSubmitUploadFile = async () => {
    if (!uploadFile) {
      return handleOpenWithErrorMessage('ファイルを選択してください')
    }
    const body = [
      {
        file: {
          filename: uploadFile.filename,
          path: uploadFile.url,
        },
        name: fileMeta?.name ? fileMeta.name : fileType !== 'OTHER' ? fileType : 'ファイル名を入力',
        displayOrder: displayOrder,
      },
    ]
    // @ts-ignore
    if (fileType !== 'OTHER') delete body[0].displayOrder
    try {
      await containerRepository.postContainerFiles(containerId, convertFileType(fileType), body)
      handleOpenWithSuccessMessage('ファイルをアップロードしました')
      mutate([`/containers/${containerId}`, containerId])
      setOpen(false)
    } catch (e) {
      console.log(e)
      handleOpenWithErrorMessage('ファイルのアップロードに失敗しました')
    }
  }

  const handleClickCancelButton = () => {
    setLabelValue(fileMeta?.name || '')
  }

  /**
   * handleSubmitDeleteFile
   */
  const handleSubmitDeleteFile = async (fileId: number) => {
    try {
      await containerRepository.deleteContainerFiles(fileId)
      handleOpenWithSuccessMessage('ファイルを削除しました')
    } catch (e) {
      console.log(e)
      handleOpenWithErrorMessage('ファイルの削除に失敗しました')
    }
  }

  return (
    <>
      <StyledBox>
        {fileType === 'OTHER' ? (
          <StyledBoxOther>
            <StyledTypographyOther>{labelValue === '' ? label : labelValue}</StyledTypographyOther>
            <StyledIconButtonOther color="primary" onClick={() => setOpenInput(true)}>
              <DriveFileRenameOutlineOutlinedIcon sx={{ fontSize: '18px' }} />
            </StyledIconButtonOther>
          </StyledBoxOther>
        ) : (
          <StyledTypography>{label}</StyledTypography>
        )}
        <Box marginLeft={'32px'}>
          {uploadFile ? (
            <FileUploadStatusLabel
              file={uploadFile?.filename}
              handleDelete={() => {
                handleFileDelete()
                if (fileMeta?.id) {
                  handleSubmitDeleteFile(fileMeta?.id)
                }
              }}
            />
          ) : (
            <>
              <FileUploadStatusLabel file={uploadFile} loading={isFileUpLoading} />
              <Button variant="outlined" onClick={() => setOpen(true)}>
                <UploadIcon fontSize={'small'} />
                <StyledTypographyUpload>
                  {t('container_order_detail.upload')}
                </StyledTypographyUpload>
              </Button>
            </>
          )}
        </Box>
      </StyledBox>
      <InputDialog
        inputError={false}
        tempValue={labelValue !== 'ファイル名を入力' ? labelValue : ''}
        dialogToggleState={openInput}
        setTempValue={setLabelValue}
        setDialogToggleState={() => setOpenInput(false)}
        handleClickDialogCancel={handleClickCancelButton}
        handleSetValue={() => handleSubmitUpdateFileName()}
        dialogTitle={'ファイル名を入力'}
        formCategory={'string'}
      />
      <FileUploaderDialog
        dialogToggleState={open}
        closeDialog={() => setOpen(false)}
        dialogTitle={t('container_order_detail.file_upload')}
        fileText={t('container_order_detail.drop_your_files_here_or')}
        uploadText={t('container_order_detail.upload')}
        isError={false}
        isLoading={isFileUpLoading}
        onSaveFile={handleFileChange}
      />
    </>
  )
}
